import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FlatTreeControl } from '@angular/cdk/tree';
import { TranslateService } from '@ngx-translate/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { AppService } from 'src/app/_services/app.service';

interface Redirect {
  url: string;
  marketId?: string;
}

/**
 * Reference from Angular Material Tree Nodes
 */
interface SideListNode {
  expandable: boolean;
  name: string;
  icon?: string;
  childMenus?: SideListNode[];
  redirect?: Redirect;
  url?: string;
  addon: boolean;
  order?: string;
  hovered?: boolean;
  level: number;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideVertical', [
      state('closed', style({ height: '0', overflow: 'hidden' })),
      state('open', style({ height: '*', overflow: 'hidden' })),
      transition('closed <=> open', animate('300ms ease-in-out'))
    ])
    ],
  standalone: false
})

export class SidebarComponent {

  private _transformer = (node: SideListNode, level: number) => {
    return {
      expandable: !!node.childMenus && node.childMenus.length > 0,
      name: node.name,
      icon: node.icon,
      redirect: node.redirect,
      url: node.url,
      addon: node.addon,
      order: node.order,
      hovered: node.hovered,
      level: level
    };
  };

  treeControl = new FlatTreeControl<SideListNode>(
    node => node.level,
    node => node.expandable,
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.childMenus,
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  constructor(private router: Router, public service: AppService, private translate: TranslateService, @Inject('BASE_URL') private baseUrl: string, private http: HttpClient) {
    const currentLanguage = this.translate.getBrowserLang();
    this.http.get<SideListNode[]>(baseUrl + 'api/common/getSidebarMenu/' + currentLanguage).subscribe(result => {
      const TREE_DATA: SideListNode[] = this.InitializeNodes(result);
      let MY_GEN_MARKETS: SideListNode[] = [];
      if (service.currentUser.userContributeMarkets.length > 0 || service.currentUser.userAssessMarkets.length > 0) {
        if (service.currentUser.userContributeMarkets.length > 0) {
          service.currentUser.userContributeMarkets.forEach(o => {
            MY_GEN_MARKETS.push({
              expandable: false,
              name: o.marketCode,
              redirect: {
                url: o.isTransactional === true ? 'contribute/contributeTransact' : 'contribute/contribute',
                marketId: o.marketId
              },
              addon: false, 
              order: o.marketCode + 'A',
              level: 0
            });
          });
        }
        if (service.currentUser.userAssessMarkets.length > 0) {
          service.currentUser.userAssessMarkets.forEach(o => {
            MY_GEN_MARKETS.push({
              expandable: false,
              name: o.marketCode,
              redirect: {
                url: o.isTransactional === true ? 'contribute/assessTransact' : 'contribute/assess',
                marketId: o.marketId
              },
              addon: false, 
              order: o.marketCode + 'B',
              level: 0
            });
          });
        }
        MY_GEN_MARKETS = MY_GEN_MARKETS.sort((e1, e2) => (e1.order > e2.order) ? 1 : -1);
      }
      const myMarket = TREE_DATA.find(ele => ele.name === 'My Markets' || ele.name === 'Mis Mercados');
      if (MY_GEN_MARKETS.length > 0 && myMarket) {
        if (myMarket.childMenus) {
          myMarket.childMenus = MY_GEN_MARKETS.concat(myMarket.childMenus);
        } else {
          myMarket.childMenus = MY_GEN_MARKETS;
        }
      } else if (myMarket) {
        myMarket.childMenus = null; // removing supporting docs when there are no markets to contribute
        TREE_DATA.splice(TREE_DATA.indexOf(myMarket), 1);
      }
      this.dataSource.data = TREE_DATA;
      const cookie = this.service.getCookie('sidebar_preference');
      if (cookie) {
        const blnCookie = this.service.getBoolean(cookie);
        if (blnCookie !== null) {
          this.service.miniSidebarOpen = blnCookie;
        }
      }
    }, error => {
      console.error(error);
    });
  }


  /** Initializes all the nodes */
  InitializeNodes(menulist: SideListNode[]): SideListNode[] {
    if (menulist.length > 0) {
      menulist.forEach(menu => {
        menu.hovered = false;
      });
    }
    return menulist;
  }

  hasChild = (_: number, node: SideListNode) => node.expandable;
  //hasChild = (_: number, node: SideListNode) => !!node.childMenus && node.childMenus.length > 0;

  // tracks which node is currently open in mini mode
  hoveredNode: SideListNode | null = null;
  isHoveringTrigger = false;
  isHoveringDropdown = false;

  // called by (mouseenter) on the trigger
  onMouseEnterTrigger(node: SideListNode) {
    if (!this.service.miniSidebarOpen) return;
    this.hoveredNode = node;
    this.isHoveringTrigger = true;
  }

  // called by (mouseleave) on the trigger
  onMouseLeaveTrigger() {
    this.isHoveringTrigger = false;
    this.checkHoverState();
  }

  // called by (mouseenter) on the dropdown panel
  onMouseEnterDropdown() {
    this.isHoveringDropdown = true;
  }

  // called by (mouseleave) on the dropdown panel
  onMouseLeaveDropdown() {
    this.isHoveringDropdown = false;
    this.checkHoverState();
  }

  private checkHoverState() {
    // if neither trigger nor dropdown is hovered, close it
    if (!this.isHoveringTrigger && !this.isHoveringDropdown) {
      this.hoveredNode = null;
    }
  }

  /**
   * Navigate between pages by using sidebar selection
   * @param node The node to use to determine where to send th user
   */
  navigateToPage(node: SideListNode) {
    if (node.redirect) {
      this.service.addOnUrl = '';
      if (node.redirect.marketId) {
        this.router.navigate([node.redirect.url], { queryParams: { ID: node.redirect.marketId } });
      } else {
        this.router.navigate([node.redirect.url]);
      }
    } else if (node.url) {
      this.service.addOnUrl = '';
      if (node.addon) {
        this.service.addOnUrl = node.url;
        // Adds the external URL for iFrame
        this.router.navigate(['addon']);
      } else {
        this.router.navigate([node.url]);
      }
    }
  }

  /** Using to close and open sidebar */
  toggleSidebar() {
    this.service.miniSidebarOpen = !this.service.miniSidebarOpen;
    this.treeControl.collapseAll();

    this.service.checkSidebarCookie(1);
  }
  /** On mouse over event function */
  OnMouseOver(node: SideListNode) {
    if (this.service.miniSidebarOpen) {
      this.OnMouseOut();
      node.hovered = true;
    }
  }

  /** On mouse out event function */
  OnMouseOut() {
    if (this.service.miniSidebarOpen) {
      this.dataSource.data.forEach(ele => {
        ele.hovered = false;
      });
    }
  }
}
